import { useReducer, useEffect } from 'react'
import api from '@api'
import { Order, IPageInfo, IWorkplaceGroupTableRecord } from '@api/types'
import { uniqueId } from 'lodash'
import { useSelection } from '@hooks'
import { ISelection } from '@hooks/useSelection'
import { useShouldReload, useReload } from '@hooks/reloadContext'
import { IWorkplaceGroupVariables } from '@api/workplaceGroup/list'
import { Toaster } from '@ui'
import { ApolloError } from 'apollo-boost'

const PAGE_SIZE = 25
const MAX_PAGE_SIZE = 150

enum Actions {
  LOAD_START,
  LOAD_START_WITHOUT_SKELETON,
  LOAD,
  SORT,
  NEXT_PAGE,
  QUERY,
  ERROR,
  OPTIMISTIC_DELETE,
}

interface LoadStartAction {
  type: Actions.LOAD_START
  reqId: string
}

interface LoadStartActionWithoutSkeleton {
  type: Actions.LOAD_START_WITHOUT_SKELETON
  reqId: string
}

interface LoadAction {
  type: Actions.LOAD
  reqId: string
  workplaceGroups: IWorkplaceGroupTableRecord[]
  count: number
  pageInfo: IPageInfo
}

interface SortAction {
  type: Actions.SORT
  column: string
}

interface NextPageAction {
  type: Actions.NEXT_PAGE
}

interface QueryAction {
  type: Actions.QUERY
  query: string
}

interface ErrorAction {
  type: Actions.ERROR
  reqId: string
}
interface OptimisticDeleteAction {
  type: Actions.OPTIMISTIC_DELETE
  ids: string[]
}

export interface IDHash {
  [id: string]: boolean
}

type Action =
  | LoadAction
  | LoadStartAction
  | NextPageAction
  | SortAction
  | QueryAction
  | ErrorAction
  | LoadStartActionWithoutSkeleton
  | OptimisticDeleteAction

interface Sort {
  column: string
  order: Order
}

interface State {
  reqId: string
  isLoading: boolean
  isSorting: boolean
  isPaging: boolean
  count: number
  items: IWorkplaceGroupTableRecord[]
  after: string | null
  pageInfo: IPageInfo
  sort: Sort
  query: string
  first: number
}

export interface UseWorkplaceGroupsHook extends State {
  sortBy: (column: string) => void
  nextPage: () => void
  applyQuery: (query: string) => void
  clearQuery: () => void
  reload: () => void
  selection: ISelection
  optimisticDelete: (ids: string[]) => void
  fetchExportLink: () => Promise<string>
}

let persistedState: State = {
  reqId: uniqueId('request'),
  isLoading: true,
  isSorting: false,
  isPaging: false,
  count: 0,
  first: PAGE_SIZE,
  items: [],
  after: null,
  sort: { column: 'name', order: Order.ASC },
  pageInfo: { hasNextPage: false, endCursor: '' },
  query: '',
}

interface GetVariablesFunc {
  query: string
  sort: Sort
  after?: string | null
  first?: number
}

export function getVariables({
  query,
  sort,
  after,
  first,
}: GetVariablesFunc): IWorkplaceGroupVariables {
  return {
    after,
    orderBy: sort.column + '_' + sort.order,
    first,
    where: {
      name_contains: query,
    },
  }
}

export default function useWorkplaceGroups(): UseWorkplaceGroupsHook {
  const [state, dispatch] = useReducer(reducer, persistedState)
  const selection = useSelection()
  const shouldReload = useShouldReload()
  const reload = useReload()

  const fetchWorkplaceGroups =
    ({
      showSkeleton,
      loadAll,
      shouldClearSelection = true,
    }: {
      showSkeleton: boolean
      loadAll: boolean
      shouldClearSelection?: boolean
    }) =>
    () => {
      const reqId = uniqueId('request')
      const variables = getVariables({
        after: loadAll ? null : state.after,
        sort: state.sort,
        query: state.query,
        first: loadAll ? state.first : PAGE_SIZE,
      })

      if (showSkeleton) {
        dispatch({ type: Actions.LOAD_START, reqId })
      } else {
        dispatch({ type: Actions.LOAD_START_WITHOUT_SKELETON, reqId })
      }

      api.workplaceGroup
        .list(variables)
        .then(({ edges, counters, pageInfo }) => {
          const isAllItemsSelected = selection.hasAllItemsSelected(state.items.length)
          const itemIds: string[] = []

          const items = edges.map(({ node }) => {
            if (isAllItemsSelected) {
              itemIds.push(node.id)
            }

            return node
          })

          //clear selection outside filtered range
          if (shouldClearSelection) {
            selection.removeSelectionsByItems(items)
          }

          // select all items if the select all is checked
          if (isAllItemsSelected && items.length) {
            selection.addAll(itemIds)
          }

          dispatch({
            type: Actions.LOAD,
            count: counters.total,
            workplaceGroups: items,
            pageInfo,
            reqId,
          })
        })
        .catch((error: ApolloError) => Toaster.fail({ error }))
    }

  //api request without skeleton and with load only 25 items after cursor
  useEffect(
    fetchWorkplaceGroups({ showSkeleton: false, loadAll: false, shouldClearSelection: false }),
    [state.after]
  )

  //api request without skeleton and with load all items in state.first
  useEffect(fetchWorkplaceGroups({ showSkeleton: false, loadAll: true }), [
    state.sort,
    shouldReload.workplaceGroups,
  ])

  //api request with skeleton and with load all items in state.first
  useEffect(fetchWorkplaceGroups({ showSkeleton: true, loadAll: true }), [state.query])

  useEffect(() => {
    persistedState = { ...state, after: null }
  })

  const fetchExportLink = () => {
    return api.workplaceGroup.csv({
      orderBy: `${state.sort.column}_${state.sort.order}`,
      where: null,
    })
  }

  return {
    ...state,
    nextPage: () => {
      dispatch({ type: Actions.NEXT_PAGE })
    },
    sortBy: (column: string) => {
      dispatch({ type: Actions.SORT, column })
    },
    applyQuery: (query: string) => {
      dispatch({ type: Actions.QUERY, query })
    },
    optimisticDelete: (ids: string[]) => {
      dispatch({ type: Actions.OPTIMISTIC_DELETE, ids })
    },
    clearQuery: () => {
      dispatch({
        type: Actions.QUERY,
        query: '',
      })
    },
    fetchExportLink,
    reload: () => reload.workplaceGroups(),
    selection: selection,
  }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case Actions.LOAD_START:
      return {
        ...state,
        reqId: action.reqId,
        isLoading: true,
      }
    case Actions.LOAD_START_WITHOUT_SKELETON:
      return {
        ...state,
        reqId: action.reqId,
        isLoading: false,
      }

    case Actions.ERROR:
      return action.reqId === state.reqId
        ? { ...state, isLoading: false, isSorting: false, isPaging: false }
        : state

    case Actions.LOAD:
      return action.reqId === state.reqId
        ? {
            ...state,
            isLoading: false,
            isSorting: false,
            isPaging: false,
            count: action.count,
            items: state.isPaging
              ? [...state.items, ...action.workplaceGroups]
              : action.workplaceGroups,
            pageInfo: action.pageInfo,
          }
        : state

    case Actions.SORT:
      return {
        ...state,
        isSorting: true,
        pageInfo: { hasNextPage: false, endCursor: '' },
        sort: {
          column: action.column,
          order:
            state.sort.column === action.column
              ? state.sort.order === Order.ASC
                ? Order.DESC
                : Order.ASC
              : Order.ASC,
        },
      }

    case Actions.NEXT_PAGE:
      return {
        ...state,
        after: state.pageInfo.endCursor,
        isPaging: true,
        first: Math.min(MAX_PAGE_SIZE, state.first + PAGE_SIZE),
      }

    case Actions.QUERY:
      return {
        ...state,
        query: action.query,
        pageInfo: { hasNextPage: false, endCursor: '' },
      }
    case Actions.OPTIMISTIC_DELETE:
      return {
        ...state,
        items: state.items.filter((wpg) => !action.ids.includes(wpg.id)),
      }

    default:
      return state
  }
}
